import { useState, useEffect, useCallback } from 'react'
import { ChevronLeft, ChevronRight, Loader2, Download, Check, X, Play, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../services/api'
import {
  fetchPayoutRequests,
  approvePayoutRequest,
  rejectPayoutRequest,
  processPayoutRequest,
} from '../services/adminService'
import { exportToCsv } from '../utils/exportCsv'
import type { PayoutRequestAdmin } from '../types'
import AdminCompanyPayouts from './AdminCompanyPayouts'
import { payoutNote } from '../utils/payoutNote'

/** The list returns the whole row; the reference is what to look up in Paystack. */
type PayoutRow = PayoutRequestAdmin & { paystackReference?: string | null }

const STATUS_TABS: { value: string; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'PROCESSING', label: 'Processing' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'FAILED', label: 'Failed' },
]

const STATUS_STYLES: Record<string, { bg: string; text: string; dot: string }> = {
  PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  APPROVED: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
  PROCESSING: { bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500' },
  COMPLETED: { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  REJECTED: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
  FAILED: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
}

const METHOD_LABELS: Record<string, string> = {
  MTN_MOMO: 'MTN MoMo',
  TELECEL_CASH: 'Telecel Cash',
  AIRTELTIGO_MONEY: 'AirtelTigo',
  BANK_TRANSFER: 'Bank Transfer',
}

const PAGE_SIZE = 10

/**
 * Collectors and companies are paid on separate rails to separate parties, so
 * they get separate lists rather than one table where the wrong row could be
 * approved by mistake.
 */
type Audience = 'collectors' | 'companies'

function AudienceTabs({
  audience,
  onChange,
}: {
  audience: Audience
  onChange: (next: Audience) => void
}) {
  return (
    <div className="flex bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm w-fit">
      {([
        { value: 'collectors' as const, label: 'Collector payouts' },
        { value: 'companies' as const, label: 'Company payouts' },
      ]).map(({ value, label }) => (
        <button
          key={value}
          onClick={() => onChange(value)}
          className={`px-4 py-2 text-xs font-semibold transition-colors ${
            audience === value ? 'bg-primary-600 text-white' : 'text-[#6B7280] hover:bg-gray-50'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

/** The server says why (Paystack refused, no recipient on the account); a fixed line did not. */
function serverMessage(err: unknown, fallback: string): string {
  const message = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message
  return typeof message === 'string' && message.trim() ? message : fallback
}

export default function Payouts() {
  const [requests, setRequests] = useState<PayoutRow[]>([])
  const [total, setTotal] = useState(0)
  const [totalPaidOut, setTotalPaidOut] = useState(0)
  const [pendingCount, setPendingCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [page, setPage] = useState(1)
  const [audience, setAudience] = useState<Audience>('collectors')

  // Action loading states
  const [approvingId, setApprovingId] = useState<string | null>(null)
  const [rejectingId, setRejectingId] = useState<string | null>(null)
  const [processingId, setProcessingId] = useState<string | null>(null)
  const [checkingId, setCheckingId] = useState<string | null>(null)

  const loadPayouts = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchPayoutRequests({
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        page,
        limit: PAGE_SIZE,
      })
      setRequests(data?.requests ?? [])
      setTotal(data?.pagination?.total ?? 0)
      setTotalPaidOut(data?.totalPaidOut ?? 0)
      setPendingCount(data?.pendingCount ?? 0)
    } catch {
      toast.error('Failed to load payout requests')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, page])

  useEffect(() => { loadPayouts() }, [loadPayouts])

  const totalPages = Math.ceil(total / PAGE_SIZE)

  // Derived counts from loaded data
  const processingCount = requests.filter((r) => r.status === 'PROCESSING').length
  const completedThisMonth = requests.filter((r) => {
    if (r.status !== 'COMPLETED') return false
    const now = new Date()
    const d = new Date(r.completedAt ?? r.createdAt)
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
  }).length

  const handleApprove = async (id: string) => {
    setApprovingId(id)
    try {
      await approvePayoutRequest(id)
      toast.success('Payout request approved')
      loadPayouts()
    } catch (err) {
      toast.error(serverMessage(err, 'Failed to approve payout request'))
    } finally {
      setApprovingId(null)
    }
  }

  const handleReject = async (id: string) => {
    const reason = window.prompt('Enter rejection reason:')
    if (!reason) return
    setRejectingId(id)
    try {
      await rejectPayoutRequest(id, reason)
      toast.success('Payout request rejected')
      loadPayouts()
    } catch (err) {
      toast.error(serverMessage(err, 'Failed to reject payout request'))
    } finally {
      setRejectingId(null)
    }
  }

  const handleProcess = async (id: string) => {
    if (!confirm('Initiate Paystack transfer for this payout?')) return
    setProcessingId(id)
    try {
      const result = await processPayoutRequest(id)
      if (result?.needsOtp) {
        // Paystack holds the transfer until someone enters the OTP. A green
        // "initiated" said the money was on its way when it was not.
        toast(
          `Paystack is holding this transfer for an OTP. Finalise it in the Paystack dashboard. Reference: ${result.reference}`,
          { icon: '⚠️', duration: 12000 },
        )
      } else {
        toast.success('Payout transfer initiated')
      }
      loadPayouts()
    } catch (err) {
      toast.error(serverMessage(err, 'Failed to process payout'))
      // A send with no answer leaves the payout processing; the row on screen
      // must show that (and its Check button), not the stale "approved".
      loadPayouts()
    } finally {
      setProcessingId(null)
    }
  }

  /** Settles a payout stuck in processing from Paystack's own record of the transfer. */
  const handleCheck = async (id: string) => {
    setCheckingId(id)
    try {
      const res = await api.post(`/payouts/admin/requests/${id}/check`, undefined, { timeout: 60000 })
      toast(res.data?.message ?? 'Checked with Paystack', { duration: 8000 })
      loadPayouts()
    } catch (err) {
      toast.error(serverMessage(err, 'Could not check with Paystack'))
    } finally {
      setCheckingId(null)
    }
  }

  const handleExport = () => {
    if (!requests.length) return
    exportToCsv(
      ['ID', 'Collector', 'Email', 'Amount', 'Method', 'Account', 'Status', 'Date'],
      requests.map((r) => [
        r.id,
        r.collector?.name ?? '',
        r.collector?.email ?? '',
        String(r.amount),
        METHOD_LABELS[r.method] ?? r.method,
        r.account ? `${r.account.accountName} - ${r.account.accountNumber}` : '',
        r.status,
        new Date(r.createdAt).toLocaleDateString(),
      ]),
      'payouts'
    )
  }

  if (audience === 'companies') {
    return (
      <div className="space-y-5">
        <AudienceTabs audience={audience} onChange={setAudience} />
        <AdminCompanyPayouts />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <AudienceTabs audience={audience} onChange={setAudience} />

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {[
          { label: 'Total Paid Out', value: `GHS ${totalPaidOut.toLocaleString()}`, color: 'text-green-700' },
          { label: 'Pending Requests', value: pendingCount, color: 'text-yellow-700' },
          { label: 'Processing', value: processingCount, color: 'text-purple-700' },
          { label: 'Completed This Month', value: completedThisMonth, color: 'text-blue-700' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <p className="text-xs text-[#6B7280] font-medium">{label}</p>
            <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
        <div className="flex flex-wrap gap-1.5">
          {STATUS_TABS.map(({ value, label }) => (
            <button
              type="button"
              key={value}
              onClick={() => { setStatusFilter(value); setPage(1) }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap ${
                statusFilter === value
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-[#F8FAF9] text-[#6B7280] hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <p className="text-sm font-semibold text-[#1A1A2E]">
            {total} payout request{total !== 1 ? 's' : ''}
          </p>
          <button
            type="button"
            onClick={handleExport}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#1A1A2E] bg-gray-100 px-3 py-1.5 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Download size={13} />
            Export CSV
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F8FAF9]">
                  {['Collector', 'Amount', 'Method', 'Account', 'Status', 'Date', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {requests.map((req) => {
                  const st = STATUS_STYLES[req.status] ?? STATUS_STYLES.PENDING
                  return (
                    <tr key={req.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-xs font-medium text-[#1A1A2E]">{req.collector?.name ?? '—'}</p>
                        <p className="text-[10px] text-[#6B7280]">{req.collector?.email ?? ''}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-bold text-[#1A1A2E]">
                          GHS {req.amount?.toLocaleString() ?? 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280]">
                        {METHOD_LABELS[req.method] ?? req.method}
                      </td>
                      <td className="px-4 py-3">
                        {req.account ? (
                          <>
                            <p className="text-xs text-[#1A1A2E]">{req.account.accountName}</p>
                            <p className="text-[10px] text-[#6B7280]">
                              {req.account.accountNumber}
                              {req.account.bankName ? ` (${req.account.bankName})` : ''}
                            </p>
                          </>
                        ) : (
                          <span className="text-xs text-[#6B7280]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full w-fit ${st.bg} ${st.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                          {req.status}
                        </span>
                        {payoutNote(req) && (
                          <p className={`text-[10px] mt-0.5 ${req.status === 'FAILED' ? 'text-red-500' : 'text-[#6B7280]'}`}>{payoutNote(req)}</p>
                        )}
                        {req.paystackReference && (
                          <p className="text-[10px] text-[#6B7280] mt-0.5 font-mono break-all">Ref {req.paystackReference}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280] whitespace-nowrap">
                        {new Date(req.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          {req.status === 'PENDING' && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleApprove(req.id)}
                                disabled={approvingId === req.id}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-green-700 bg-green-50 rounded-lg hover:bg-green-100 disabled:opacity-50 transition-colors"
                              >
                                {approvingId === req.id ? (
                                  <Loader2 size={12} className="animate-spin" />
                                ) : (
                                  <Check size={12} />
                                )}
                                Approve
                              </button>
                              <button
                                type="button"
                                onClick={() => handleReject(req.id)}
                                disabled={rejectingId === req.id}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-red-600 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors"
                              >
                                {rejectingId === req.id ? (
                                  <Loader2 size={12} className="animate-spin" />
                                ) : (
                                  <X size={12} />
                                )}
                                Reject
                              </button>
                            </>
                          )}
                          {req.status === 'APPROVED' && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleProcess(req.id)}
                                disabled={processingId === req.id}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50 transition-colors"
                              >
                                {processingId === req.id ? (
                                  <Loader2 size={12} className="animate-spin" />
                                ) : (
                                  <Play size={12} />
                                )}
                                Process
                              </button>
                              {/* An approved payout that cannot be sent no longer holds its amount for good. */}
                              <button
                                type="button"
                                onClick={() => handleReject(req.id)}
                                disabled={rejectingId === req.id}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-red-600 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors"
                              >
                                {rejectingId === req.id ? (
                                  <Loader2 size={12} className="animate-spin" />
                                ) : (
                                  <X size={12} />
                                )}
                                Reject
                              </button>
                            </>
                          )}
                          {/* Paystack is asked by reference, so without one the check can only fail. */}
                          {req.paystackReference && (req.status === 'PROCESSING' || req.status === 'APPROVED') && (
                            <button
                              type="button"
                              onClick={() => handleCheck(req.id)}
                              disabled={checkingId === req.id}
                              title="Ask Paystack what happened to this transfer, and settle the payout from its answer"
                              className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 disabled:opacity-50 transition-colors whitespace-nowrap"
                            >
                              {checkingId === req.id ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <RefreshCw size={12} />
                              )}
                              Check with Paystack
                            </button>
                          )}
                          {/* Left by older code, which saved the reference only after Paystack answered. */}
                          {req.status === 'PROCESSING' && !req.paystackReference && (
                            <p className="text-[10px] text-[#6B7280] max-w-[180px]">
                              No Paystack reference was saved. Find this transfer in the Paystack dashboard by amount and recipient.
                            </p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
                {requests.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-sm text-[#6B7280]">
                      No payout requests found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
            <p className="text-xs text-[#6B7280]">
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, total)}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                aria-label="Previous page"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg text-[#6B7280] hover:bg-gray-100 disabled:opacity-40 transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((p) => (
                <button
                  type="button"
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-7 h-7 rounded-lg text-xs font-semibold transition-colors ${
                    p === page ? 'bg-primary-600 text-white' : 'text-[#6B7280] hover:bg-gray-100'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                aria-label="Next page"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || totalPages === 0}
                className="p-1.5 rounded-lg text-[#6B7280] hover:bg-gray-100 disabled:opacity-40 transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
